import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleCheck } from 'lucide-react';
import { Button } from 'components';
import { ProtectedLayout } from 'hoc';
import useCheckMicAccess from 'hooks/useCheckMicAccess';
import { SUPPORT_EMAIL } from 'utils/supportEmail';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { formatReadableDate, formatReadableTime } from 'utils';
import { DismissButton, ErrorStack, ImmunizationSlot, Wrapper } from './recording.styles';
import { PatientHeader } from 'widgets';
import { RecordingBottomNavbar } from 'widgets/recording/RecordingBottomNavbar';
import { WithoutRecordingBottomNavbar } from 'widgets/recording/WithoutRecordingBottomNavbar';
import { RecordingNotes } from 'widgets/recording/RecordingNotes';
import { ImmunizationsSection, VaccinationDialog } from 'widgets/immunization';
import { useImmunizationEditor } from 'hooks/useImmunizationEditor';
import type { PreventionAlert } from 'types/preventionAlerts';
import { useManualNotes } from 'hooks/useManualNotes';
import type { ResumedState } from 'hooks/useRecordingWebSocket';
import { useHomeFilters } from 'providers';
import { usePatientSync } from 'providers/PatientSyncProvider';
import { useAnamnesisLookup } from 'hooks/useAnamnesisLookup';
import { useLanguages } from 'hooks/languages';
import { useRecordingLeaveGuard } from 'hooks/useRecordingLeaveGuard';
import { useCancelRecording } from 'hooks/useCancelRecording';
import { CancelRecordingDialog } from 'widgets/recording/CancelRecordingDialog';
import { SummaryProcessingBar } from 'widgets/recording/SummaryProcessingBar';
import { useVisitReasons } from 'hooks/visitReasons';
import { useQuery } from '@tanstack/react-query';
import { type ExportStatus, getExportStatus } from 'api/exportStatus';
import {
  buildPatientNameLine,
  buildPatientSubline,
  formatPatientName,
} from 'utils/visitReasonDisplay';
import { toast } from 'sonner';
import { trackError } from 'utils/trackError';
import { getApiErrorMessage } from 'utils/apiError';
import { RecordingPhase, useRecording } from 'hooks/useRecording';
import { useRecordingObjectionStatus } from 'hooks/useRecordingObjectionStatus';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import type { ResultData } from 'types/result';

interface LocationState {
  interactionId?: string;
  startPaused?: boolean;
  previousElapsedTime?: number;
  languageCode?: string;
  fromPatientOverview?: boolean;
  autoResume?: boolean;
  autoEnd?: boolean;
  withoutRecording?: boolean;
}

export default function RecordingScreen() {
  const location = useLocation();
  const { medatixxPatientId } = useParams<{ medatixxPatientId: string }>();
  const interactionId = (location.state as LocationState | null)?.interactionId;

  return <RecordingScreenContent key={`${medatixxPatientId ?? ''}:${interactionId ?? ''}`} />;
}

function RecordingScreenContent() {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const { medatixxPatientId } = useParams<{ medatixxPatientId: string }>();
  const locationState = location.state as LocationState;
  const interactionIdFromLocation = locationState?.interactionId;
  const startPaused = locationState?.startPaused ?? false;
  const seedElapsedTime = locationState?.previousElapsedTime ?? 0;
  const withoutRecording = !!locationState?.withoutRecording;

  const {
    micPermissionDenied,
    micDeviceNotFound: initialDeviceNotFound,
    dismiss,
  } = useCheckMicAccess(!withoutRecording);

  const { data: languagesData } = useLanguages();
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string | undefined>(
    locationState?.languageCode
  );
  const { resetFilters } = useHomeFilters();
  const {
    data: anamnesisLookupData,
    error: anamnesisLookupError,
    isLoading: isAnamnesisLoading,
  } = useAnamnesisLookup(medatixxPatientId);
  const currentAnamnesis = anamnesisLookupData ?? null;
  const { data: rawVisitReasons } = useVisitReasons(currentAnamnesis?.medatixxPatientId);
  const [selectedVisitReason, setSelectedVisitReason] = useState(currentAnamnesis?.visitReason);

  useEffect(() => {
    if (currentAnamnesis?.visitReason && !selectedVisitReason) {
      setSelectedVisitReason(currentAnamnesis.visitReason);
    }
  }, [currentAnamnesis?.visitReason, selectedVisitReason]);

  useEffect(() => {
    if (!anamnesisLookupError) return;
    trackError(anamnesisLookupError, { context: 'manualAnamnesisLookup' });
    toast.error(t('home.anamnesisLookupDialog.toastError'));
  }, [anamnesisLookupError, t]);

  const openResult = useCallback(
    (result: ResultData, state: Record<string, unknown> = {}) => {
      resetFilters();
      void navigate(`/result/${encodeURIComponent(result.id)}`, {
        state: { result, languageCode: selectedLanguageCode, ...state },
      });
    },
    [navigate, resetFilters, selectedLanguageCode]
  );

  const onRecordingResult = useCallback(
    (data: unknown, elapsedTime: number) => {
      const result = data as ResultData | undefined;
      if (result?.id != null) openResult(result, { elapsedTime, fromRecording: true });
    },
    [openResult]
  );

  const manualNotes = useManualNotes();
  const { replaceNotes } = manualNotes;
  const handleResumed = useCallback(
    (state: ResumedState) => {
      replaceNotes(state.notes);
      if (state.languageCode) setSelectedLanguageCode(state.languageCode);
    },
    [replaceNotes]
  );

  const { data: recordingObjection, isFetched: isObjectionFetched } =
    useRecordingObjectionStatus(medatixxPatientId);
  const isObjectionKnown = !medatixxPatientId || isObjectionFetched;
  const cameFromPatientOverview = !!locationState?.fromPatientOverview;
  const autoResume = !!locationState?.autoResume;
  const autoEnd = !!locationState?.autoEnd;
  const autoStart = withoutRecording
    ? !isAnamnesisLoading
    : !autoEnd &&
      (cameFromPatientOverview || autoResume) &&
      isObjectionKnown &&
      !recordingObjection?.active;

  const [influenzaDialogAlert, setInfluenzaDialogAlert] = useState<PreventionAlert | null>(null);

  const {
    recordingPhase,
    elapsedTime,
    interactionId,
    mediaStream,
    webSocketError,
    deviceNotFound,
    browserNotSupported,
    reconnectEpisode,
    isProcessing,
    startRecording,
    continueWithoutRecording,
    isContinuingWithoutRecording,
    toggleRecording,
    pauseRecording,
    isPausing,
    stopRecording,
    stopWithoutRecording,
    isWithoutRecordingStarted,
    scheduleNotesSync,
    dismissDeviceNotFound,
    abandonRecording,
  } = useRecording({
    interactionIdFromLocation,
    startPaused,
    initialElapsedTime: seedElapsedTime,
    initialDeviceNotFound,
    selectedLanguageCode,
    currentAnamnesis,
    medatixxPatientId,
    selectedVisitReason,
    notes: manualNotes.notes,
    autoStart,
    autoEnd,
    withoutRecording,
    micPermissionDenied,
    onSuccess: onRecordingResult,
    onResumed: handleResumed,
  });

  const { immunizations, createImmunization, updateImmunization, removeImmunization } =
    useImmunizationEditor({ interactionId, medatixxPatientId });
  const immunizationsBlockRef = useRef<HTMLDivElement>(null);

  const cancelRecording = useCancelRecording({
    elapsedTime,
    interactionId,
    notes: manualNotes.notes,
    hasImmunizations: immunizations.length > 0,
    pauseRecording,
    abandonRecording,
  });

  const { registerRecordingPause } = usePatientSync();
  const [isLeavingForPatientSwitch, setIsLeavingForPatientSwitch] = useState(false);
  useEffect(
    () =>
      registerRecordingPause(() => {
        const paused = pauseRecording();
        setIsLeavingForPatientSwitch(true);
        return paused;
      }),
    [pauseRecording, registerRecordingPause]
  );

  const handleAutoPauseOnLeave = useCallback(async () => {
    const wasRecording = recordingPhase === RecordingPhase.RECORDING;
    await pauseRecording();
    if (!wasRecording) return;
    trackEvent(ANALYTICS_EVENTS.PATIENT_AUTO_PAUSE_RECORDING);
    toast.info(t('recording.autoPaused'));
  }, [recordingPhase, pauseRecording, trackEvent, t]);

  // A manual pause still waiting for its ack keeps the guard on, so leaving waits for it.
  const isRecordingActive = recordingPhase === RecordingPhase.RECORDING || isPausing;
  useRecordingLeaveGuard(
    isRecordingActive && !isLeavingForPatientSwitch && !cancelRecording.isCancelled,
    handleAutoPauseOnLeave
  );

  const { isCancelled } = cancelRecording;
  useEffect(() => {
    if (!isCancelled) return;
    toast.info(t('recording.cancelled'), {
      closeButton: true,
      icon: <CircleCheck className="size-4" aria-hidden />,
      style: { '--normal-bg': 'var(--vantis-pastelblue)' } as CSSProperties,
    });
    void navigate(
      medatixxPatientId ? `/patient-overview/${encodeURIComponent(medatixxPatientId)}` : '/',
      { replace: true }
    );
  }, [isCancelled, medatixxPatientId, navigate, t]);

  useEffect(() => {
    // A resumed consultation keeps its language, even one the user has since disabled.
    if (interactionIdFromLocation !== undefined) return;
    const availableLanguages = languagesData?.languages ?? [];

    if (!availableLanguages.length) {
      if (selectedLanguageCode !== undefined) {
        setSelectedLanguageCode(undefined);
      }
      return;
    }

    if (
      selectedLanguageCode &&
      availableLanguages.some((language) => language.code === selectedLanguageCode)
    ) {
      return;
    }

    const fallbackLanguage = languagesData?.defaultLanguageCode ?? availableLanguages[0]?.code;

    if (fallbackLanguage && fallbackLanguage !== selectedLanguageCode) {
      setSelectedLanguageCode(fallbackLanguage);
    }
  }, [
    interactionIdFromLocation,
    languagesData?.defaultLanguageCode,
    languagesData?.languages,
    selectedLanguageCode,
  ]);

  const patientHeader = useMemo(() => {
    if (!currentAnamnesis) {
      return {
        title: t('recording.title'),
      };
    }
    return {
      title: formatPatientName(currentAnamnesis.patientName),
      subtitle: buildPatientSubline(currentAnamnesis.birthdate, currentAnamnesis.medatixxPatientId),
    };
  }, [currentAnamnesis, t]);

  const { data: exportStatus } = useQuery({
    queryKey: ['exportStatus', currentAnamnesis?.summary?.id],
    queryFn: () =>
      currentAnamnesis?.summary?.id
        ? getExportStatus(currentAnamnesis.summary.id)
        : Promise.resolve([]),
    enabled: !!currentAnamnesis?.summary?.id,
  });

  const isSelfAnamnesisExported = useMemo(() => {
    return exportStatus?.some(
      (status: ExportStatus) => status.fieldName === 'selfAnamnesis' && status.success
    );
  }, [exportStatus]);

  if (webSocketError) {
    const isNoFacts = webSocketError.code === 'no_facts';
    const prefixKey = isNoFacts
      ? 'recording.stopError.noFacts.prefix'
      : 'recording.stopError.otherError.prefix';
    const suffixKey = isNoFacts
      ? 'recording.stopError.noFacts.suffix'
      : 'recording.stopError.otherError.suffix';
    const now = new Date();
    const errorMessage = `${t(prefixKey)} ${SUPPORT_EMAIL}${t(suffixKey)}`;
    const mailSubject = t('recording.stopError.mailSubject', { code: webSocketError.code });
    const mailBody = t('recording.stopError.mailBody', {
      errorMessage,
      timestamp: `${formatReadableDate(now)} ${formatReadableTime(now.toISOString())}`,
      code: webSocketError.code,
      correlationId: webSocketError.correlationId ?? t('recording.stopError.unknownErrorId'),
    });
    const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

    return (
      <ProtectedLayout
        centered
        title={patientHeader.title}
        subtitle={patientHeader.subtitle}
        hasBackButton={false}
      >
        <ErrorStack>
          <h1>
            {t(prefixKey)}{' '}
            <a href={mailtoHref} className="text-primary underline-offset-4 hover:underline">
              {SUPPORT_EMAIL}
            </a>
            {t(suffixKey)}
          </h1>
          <DismissButton
            onClick={() => {
              void navigate('/');
            }}
          >
            {t('general.dismiss')}
          </DismissButton>
        </ErrorStack>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout
      centered
      title={patientHeader.title}
      subtitle={patientHeader.subtitle}
      hasBackButton={false}
    >
      <Wrapper inert={isProcessing}>
        <PatientHeader
          anamnesis={currentAnamnesis}
          showPreventionAlerts
          preventions={{
            screen: 'recording',
            defaultOpen: true,
            interactionId,
            onRecordInfluenza: setInfluenzaDialogAlert,
          }}
          visitReason={selectedVisitReason}
          availableVisitReasons={rawVisitReasons?.results ?? []}
          onVisitReasonChange={setSelectedVisitReason}
          visitReasonAccordionDefaultOpen
          collapsibleSummary={isSelfAnamnesisExported || !!currentAnamnesis?.summary?.seen}
          showSections={!micPermissionDenied}
        />
        {micPermissionDenied ? (
          <div>
            <h1>{t('errors.recordingPermissionRequired')}</h1>
            <Button onClick={dismiss} aria-label={t('general.dismiss')}>
              {t('general.dismiss')}
            </Button>
          </div>
        ) : null}
        {!micPermissionDenied ? (
          <RecordingNotes
            notes={manualNotes.notes}
            canDictate={withoutRecording}
            onSectionChange={(section, value) => {
              manualNotes.setSection(section, value);
              if (recordingPhase !== RecordingPhase.IDLE) scheduleNotesSync();
            }}
          />
        ) : null}
        {!micPermissionDenied ? (
          <ImmunizationSlot>
            <ImmunizationsSection
              ref={immunizationsBlockRef}
              immunizations={immunizations}
              onUpdate={updateImmunization}
              onRemove={removeImmunization}
            />
          </ImmunizationSlot>
        ) : null}
        {influenzaDialogAlert && interactionId && medatixxPatientId ? (
          <VaccinationDialog
            availableReasons={influenzaDialogAlert.availableVaccinationReasons ?? []}
            patientLine={buildPatientNameLine(
              currentAnamnesis?.patientName,
              currentAnamnesis?.birthdate,
              medatixxPatientId
            )}
            onSubmit={createImmunization}
            focusAfterClose={immunizationsBlockRef}
            onClose={() => setInfluenzaDialogAlert(null)}
          />
        ) : null}
      </Wrapper>

      {isProcessing ? (
        <SummaryProcessingBar />
      ) : withoutRecording ? (
        <WithoutRecordingBottomNavbar
          recordingPhase={recordingPhase}
          elapsedTime={elapsedTime}
          onTogglePause={toggleRecording}
          onStop={stopWithoutRecording}
          canStop={isWithoutRecordingStarted}
        />
      ) : (
        <RecordingBottomNavbar
          recordingPhase={recordingPhase}
          elapsedTime={elapsedTime}
          mediaStream={mediaStream}
          selectedLanguageCode={selectedLanguageCode}
          languagesData={languagesData}
          deviceNotFound={deviceNotFound}
          onDismissDeviceNotFound={dismissDeviceNotFound}
          browserNotSupported={browserNotSupported}
          reconnectEpisode={reconnectEpisode}
          medatixxPatientId={medatixxPatientId}
          recordingObjection={recordingObjection}
          isContinuingWithoutRecording={isContinuingWithoutRecording}
          languageLocked={interactionIdFromLocation !== undefined}
          actionsDisabled={cancelRecording.isCancelling}
          onToggleRecording={toggleRecording}
          onStopRecording={stopRecording}
          onStartRecording={() => void startRecording()}
          onContinueWithoutRecording={() => {
            continueWithoutRecording().catch((error: unknown) =>
              toast.error(getApiErrorMessage(error, t))
            );
          }}
          onCancelRecording={cancelRecording.requestCancel}
          onLanguageChange={setSelectedLanguageCode}
        />
      )}
      {cancelRecording.isConfirming && (
        <CancelRecordingDialog
          isCancelling={cancelRecording.isCancelling}
          onConfirm={cancelRecording.confirmCancel}
          onClose={cancelRecording.dismissConfirmation}
        />
      )}
    </ProtectedLayout>
  );
}
